// Auth page hooks: AuthError, AuthCard, DotMatrixBg
// Only active on login/signup pages. Heavy animation + WebGL.

import gsap from "gsap";
import bgVert from "../../glsl/fullscreen.vert?raw";
import bgFrag from "../../glsl/dotmatrix.frag?raw";
import type { LiveViewHook } from "./types";

// Auth hooks don't emit or listen on the push/handle channel — narrow to
// `never` so any future caller of `pushEvent` / `handleEvent` is forced
// to declare its domain in ui/auth.dox first.
type AuthHookLike = LiveViewHook<never>;

// --- AuthError: 3D fold-in + text scramble for login failure ---
// Container folds open in 3D perspective, then text scrambles through
// random cyber-glyphs character-by-character before resolving.

export const CYBER_GLYPHS = "!@#$%^&*()_+-=[]{}|;:,.<>?/~`01";

// Single source of truth for the per-char scramble cadence.
// `stagger` shifts each char's start; each char then cycles
// `cyclesPerChar` glyphs at `charDur` intervals before the final
// resolve. Tests import this so they don't duplicate the literals.
export const SCRAMBLE_TIMING = {
  stagger: 0.02,
  cyclesPerChar: 6,
  charDur: 0.035,
} as const;

// Pure: compute total scramble animation duration from text length
export const scrambleDuration = (textLength: number): number => {
  if (textLength <= 0) return 0;
  const { stagger, cyclesPerChar, charDur } = SCRAMBLE_TIMING;
  return (textLength - 1) * stagger + cyclesPerChar * charDur;
};

const scrambleText = (
  el: HTMLElement,
  finalText: string,
): gsap.core.Timeline => {
  const chars = finalText.split("");
  el.innerHTML = chars
    .map(
      (_, i) =>
        `<span data-idx="${i}" style="display:inline-block; min-width:0.5ch">\u00A0</span>`,
    )
    .join("");
  const spans = Array.from(el.querySelectorAll("span"));
  const { stagger, cyclesPerChar, charDur } = SCRAMBLE_TIMING;

  const tl = gsap.timeline();
  spans.forEach((span, i) => {
    const t0 = i * stagger;
    for (let c = 0; c < cyclesPerChar; c++) {
      tl.call(
        () => {
          span.textContent =
            CYBER_GLYPHS[Math.floor(Math.random() * CYBER_GLYPHS.length)] ??
            null;
        },
        [],
        t0 + c * charDur,
      );
    }
    tl.call(
      () => {
        span.textContent = chars[i] ?? null;
      },
      [],
      t0 + cyclesPerChar * charDur,
    );
  });
  return tl;
};

export const AuthError = {
  mounted(this: AuthHookLike) {
    const el = this.el;
    const textEl = el.querySelector("p") as HTMLElement | null;
    if (!textEl) return;
    const finalText = textEl.textContent || "";

    // Wrap element needs perspective for the 3D fold
    const parent = el.parentElement;
    if (parent) parent.style.perspective = "600px";

    // Single timeline — everything sequenced
    const tl = gsap.timeline();

    // Phase 1: 3D fold-in from top edge, like a panel flipping open
    gsap.set(el, {
      rotationX: -90,
      transformOrigin: "top center",
      opacity: 0,
      scaleY: 0.8,
    });
    tl.to(el, {
      rotationX: 0,
      opacity: 1,
      scaleY: 1,
      duration: 0.35,
      ease: "back.out(1.4)",
    })

      // Phase 2: text scramble — characters cycle through glyphs with stagger
      .add(scrambleText(textEl, finalText))

      // Phase 3: post-resolve glitch — quick x-jitter with brightness spikes
      .to(el, {
        x: 4,
        skewX: -3,
        filter: "brightness(2) hue-rotate(15deg)",
        duration: 0.04,
        ease: "none",
      })
      .to(el, {
        x: -3,
        skewX: 2,
        filter: "brightness(1.6) hue-rotate(-10deg)",
        duration: 0.03,
        ease: "none",
      })
      .to(el, {
        x: 2,
        skewX: -1,
        filter: "brightness(2.2)",
        duration: 0.03,
        ease: "none",
      })
      .to(el, {
        x: 0,
        skewX: 0,
        filter: "none",
        duration: 0.12,
        ease: "power2.out",
      });
  },
};

// --- AuthCard: 3D card flip between login/signup with height animation ---
// Full flip: old content rotates 0°→90° (first half), overlay removed at
// the invisible midpoint, new content rotates -90°→0° (second half).
// The overlay is INSIDE the card so the whole box rotates as one unit.

// Pure: map auth mode to tab index
export const tabIndexForMode = (mode: string): number =>
  mode === "login" ? 0 : 1;

// DOM: build an overlay from snapshotted HTML, stripping ids and hooks
// so LiveView doesn't try to manage the cloned nodes.
export const createOverlay = (html: string): HTMLDivElement => {
  const overlay = document.createElement("div");
  overlay.innerHTML = html;
  overlay.querySelectorAll("[id]").forEach((n) => {
    n.removeAttribute("id");
  });
  overlay.querySelectorAll("[phx-hook]").forEach((n) => {
    n.removeAttribute("phx-hook");
  });
  overlay.style.cssText =
    "position:absolute; inset:0; padding:inherit; background:inherit;" +
    "border-radius:inherit; z-index:1; pointer-events:none; overflow:hidden;";
  return overlay;
};

// Slider position measurement
interface SliderRect {
  readonly left: number;
  readonly width: number;
}

export interface TabSlider {
  readonly el: HTMLElement;
  readonly from: SliderRect;
  readonly to: SliderRect;
}

// DOM: create an animated tab highlight slider inside the overlay's tab bar.
// Returns null when the expected two-button tab bar isn't found.
export const setupTabSlider = (
  overlay: HTMLDivElement,
  oldMode: string,
  newMode: string,
): TabSlider | null => {
  const tabBar = overlay.querySelector<HTMLElement>(".flex.mb-6");
  const btns = tabBar ? Array.from(tabBar.querySelectorAll("button")) : [];
  if (!tabBar || btns.length !== 2) return null;

  const fromBtn = btns[tabIndexForMode(oldMode)]!;
  const toBtn = btns[tabIndexForMode(newMode)]!;

  btns.forEach((btn) => {
    btn.classList.remove("bg-base", "shadow", "text-text");
    btn.classList.add("text-text-muted");
  });

  tabBar.style.position = "relative";
  const slider = document.createElement("div");
  slider.className = "bg-base shadow rounded-md";
  slider.style.cssText =
    "position:absolute; top:4px; height:calc(100% - 8px); pointer-events:none; z-index:0;";
  tabBar.insertBefore(slider, tabBar.firstChild);
  btns.forEach((btn) => {
    btn.style.position = "relative";
    btn.style.zIndex = "1";
  });

  const from = { left: fromBtn.offsetLeft, width: fromBtn.offsetWidth };
  const to = { left: toBtn.offsetLeft, width: toBtn.offsetWidth };
  gsap.set(slider, { ...from });

  return { el: slider, from, to };
};

// GSAP: build the full card-flip timeline
const FLIP_HALF = 0.2;

const buildFlipTimeline = (
  el: HTMLElement,
  overlay: HTMLDivElement,
  tab: TabSlider | null,
  newH: number,
  onComplete: () => void,
): gsap.core.Timeline => {
  const tl = gsap.timeline({ onComplete });

  if (tab) {
    tl.to(tab.el, { ...tab.to, duration: FLIP_HALF, ease: "power2.inOut" }, 0);
  }
  tl.to(el, { rotateY: 90, duration: FLIP_HALF, ease: "power1.in" }, 0)
    .call(() => {
      overlay.remove();
      gsap.set(el, { rotateY: -90 });
    })
    .to(el, {
      rotateY: 0,
      height: newH,
      duration: FLIP_HALF,
      ease: "power1.out",
    })
    .set(el, { height: "auto", overflow: "", position: "" });

  return tl;
};

interface AuthCardState extends AuthHookLike {
  _mode: string;
  _tl: gsap.core.Timeline | null;
  _oldMode: string;
  _oldHeight: number;
  _oldHTML: string;
}

export const AuthCard = {
  mounted(this: AuthCardState) {
    this._mode = this.el.dataset["mode"] ?? "login";
    this._tl = null;
  },

  beforeUpdate(this: AuthCardState) {
    this._oldMode = this._mode;
    this._oldHeight = this.el.offsetHeight;
    this._oldHTML = this.el.innerHTML;
    if (this._tl) {
      this._tl.kill();
      this._tl = null;
    }
  },

  updated(this: AuthCardState) {
    const newMode = this.el.dataset["mode"] ?? "login";
    if (newMode === this._oldMode) {
      this._mode = newMode;
      return;
    }
    this._mode = newMode;

    const el = this.el;
    const newH = el.offsetHeight;

    const overlay = createOverlay(this._oldHTML);
    el.style.position = "relative";
    el.appendChild(overlay);
    gsap.set(el, {
      height: this._oldHeight,
      overflow: "hidden",
      transformPerspective: 2500,
    });

    const tab = setupTabSlider(overlay, this._oldMode, newMode);

    this._tl = buildFlipTimeline(el, overlay, tab, newH, () => {
      gsap.set(el, {
        clearProps: "rotateY,height,overflow,transformPerspective,position",
      });
      this._tl = null;
    });
  },
};

// --- DotMatrixBg: WebGL2 dot matrix shader wallpaper ---

const compileShader = (
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader => {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile: ${info}`);
  }
  return shader;
};

const linkProgram = (
  gl: WebGL2RenderingContext,
  vert: string,
  frag: string,
): WebGLProgram => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
  const prog = gl.createProgram() as WebGLProgram | null; // createProgram() returns null on context loss
  if (!prog) throw new Error("Failed to create WebGL program");
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog);
    gl.deleteProgram(prog);
    throw new Error(`Program link: ${info}`);
  }
  return prog;
};

interface DotMatrixState extends AuthHookLike {
  _raf: number | null;
  _onMove: ((e: MouseEvent) => void) | null;
}

export const DotMatrixBg = {
  mounted(this: DotMatrixState) {
    const canvas = this.el as HTMLCanvasElement;
    const gl = canvas.getContext("webgl2", {
      antialias: false,
      alpha: false,
      premultipliedAlpha: false,
    });
    if (!gl) return;

    const program = linkProgram(gl, bgVert, bgFrag);
    gl.useProgram(program);

    const uTime = gl.getUniformLocation(program, "u_time");
    const uRes = gl.getUniformLocation(program, "u_resolution");
    const uMouse = gl.getUniformLocation(program, "u_mouse");

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);

    const startTime = performance.now() / 1000;
    const mouse = [0.5, 0.5];
    const mouseTarget = [0.5, 0.5];

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseTarget[0] = (e.clientX - rect.left) / rect.width;
      mouseTarget[1] = 1.0 - (e.clientY - rect.top) / rect.height;
    };
    // Listen on document so mouse over the card still moves the shader
    document.addEventListener("mousemove", onMove);
    this._onMove = onMove;

    const tick = () => {
      if (!this._raf) return;
      const dpr = window.devicePixelRatio || 1;
      const w = (canvas.clientWidth * dpr) | 0;
      const h = (canvas.clientHeight * dpr) | 0;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      mouse[0] = mouse[0]! + (mouseTarget[0]! - mouse[0]!) * 0.05;
      mouse[1] = mouse[1]! + (mouseTarget[1]! - mouse[1]!) * 0.05;

      gl.uniform1f(uTime, performance.now() / 1000 - startTime);
      gl.uniform2f(uRes, w, h);
      gl.uniform2f(uMouse, mouse[0], mouse[1]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      this._raf = requestAnimationFrame(tick);
    };

    this._raf = requestAnimationFrame(tick);
  },

  destroyed(this: DotMatrixState) {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    if (this._onMove) {
      document.removeEventListener("mousemove", this._onMove);
    }
  },
};
